import { useState, useEffect, useMemo, FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import api from '../../services/api';

/** Comprimento mínimo de senha exigido pelo Devise (config.password_length). */
const MIN_PASSWORD_LENGTH = 6;

/**
 * ResetPasswordPage - Redefinição de senha via token (link do e-mail).
 *
 * Rota: /senha/recuperar/:token
 * Faz PUT em /condomino/recupercao-de-senha (Devise passwords#update) com
 * user[reset_password_token], user[password] e user[password_confirmation].
 *
 * Migrada do HTML server-side para React/Tailwind, com fidelidade visual à
 * ForgotPasswordPage (mesmo card, header gradiente e fundo cinza).
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token: tokenParam } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  // Token vem da rota (/senha/recuperar/:token). Mantém fallback para query
  // string (`token` / `reset_password_token`) por causa de links legados.
  const token = tokenParam || searchParams.get('token') || searchParams.get('reset_password_token') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flash, setFlash] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  // Layout diferente do login (mesmo comportamento da ForgotPasswordPage).
  useEffect(() => {
    document.body.className = 'basic-layout';
    document.documentElement.style.backgroundColor = '#EDF5F7';
    return () => {
      document.body.className = 'fix-header fix-sidebar page-water home';
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  const missingToken = useMemo(() => token.trim() === '', [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFlash(null);

    if (missingToken) {
      setFlash({
        type: 'danger',
        message: t('views.reset_password.invalid_token', 'Link de redefinição inválido ou expirado. Solicite um novo e-mail.'),
      });
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setFlash({
        type: 'danger',
        message: t('views.reset_password.min_length', 'A senha deve ter no mínimo {{count}} caracteres.', { count: MIN_PASSWORD_LENGTH }),
      });
      return;
    }

    if (password !== passwordConfirmation) {
      setFlash({
        type: 'danger',
        message: t('views.reset_password.mismatch', 'As senhas não conferem.'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.put('/condomino/recupercao-de-senha', {
        user: {
          reset_password_token: token,
          password,
          password_confirmation: passwordConfirmation,
        },
      });
      const message = response.data?.message
        || t('views.reset_password.success', 'Senha alterada com sucesso. Faça login com sua nova senha.');
      navigate('/login', { state: { flash: { type: 'success', message } } });
    } catch (err: unknown) {
      let errorMessage = t('views.reset_password.error', 'Não foi possível alterar a senha. O link pode ter expirado.');
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const data = (err as { response?: { data?: { error?: string; message?: string; errors?: Record<string, string[]> } } }).response?.data;
        if (data?.errors) {
          errorMessage = Object.values(data.errors).flat().join(', ') || errorMessage;
        } else {
          errorMessage = data?.error || data?.message || errorMessage;
        }
      }
      setFlash({ type: 'danger', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-mist-100">
      {/* Header com gradiente */}
      <div className="
        relative
        bg-gradient-to-br from-navy-900 to-navy-700
        h-[190px] max-[812px]:h-[80px]
        mb-[10px]
        py-[20px]
      ">
        <div className="text-center">
          <img src="/images/logo_login.png" alt="Laager" className="max-[812px]:h-[55px]" />
        </div>
      </div>

      {/* Container principal */}
      <div className="max-w-full px-4">
        {/* Flash message */}
        {flash && (
          <div className="flex justify-center px-[20px] pt-[20px]">
            <div
              className={`
                relative w-full max-w-[340px]
                pl-[20px] pr-10 py-[12px]
                text-white rounded-[10px]
                ${flash.type === 'success' ? '!bg-lime-600' : '!bg-alert-caution'}
              `}
              role="alert"
            >
              <strong className="block font-bold text-[13px]">
                {flash.type === 'success'
                  ? t('views.layouts.home.success', 'Sucesso!')
                  : t('views.layouts.home.attention', 'Atenção!')}
              </strong>
              <small className="text-[12px] font-normal">{flash.message}</small>
              <button
                type="button"
                className="absolute top-[10px] right-[12px] border-0 rounded-full text-[18px] leading-none font-normal text-white opacity-70 hover:opacity-100 bg-transparent cursor-pointer"
                onClick={() => setFlash(null)}
                aria-label={t('common.close', 'Fechar')}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </div>
        )}

        {/* Formulário */}
        <div className="flex justify-center p-[20px]">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-[340px] text-center bg-white rounded-[16px] shadow-[0_10px_30px_rgba(17,33,51,0.08)] px-[26px] py-[32px]"
            role="form"
          >
            <div className="text-navy-800 text-[17px] font-bold mb-[6px]">
              {t('views.reset_password.title', 'Redefinir senha')}
            </div>
            <p className="text-[12px] text-navy-700/70 mb-[22px] leading-[1.5]">
              {t('views.reset_password.subtitle', 'Escolha uma nova senha para acessar sua conta.')}
            </p>

            <PasswordInput
              value={password}
              onChange={setPassword}
              placeholder={t('views.reset_password.password_placeholder', 'Nova senha')}
              autoComplete="new-password"
              autoFocus
              showLabel={t('views.registration.show_password', 'Mostrar senha')}
              hideLabel={t('views.registration.hide_password', 'Ocultar senha')}
            />

            <PasswordInput
              value={passwordConfirmation}
              onChange={setPasswordConfirmation}
              placeholder={t('views.reset_password.confirmation_placeholder', 'Confirme a nova senha')}
              autoComplete="new-password"
              showLabel={t('views.registration.show_password', 'Mostrar senha')}
              hideLabel={t('views.registration.hide_password', 'Ocultar senha')}
            />

            <button
              type="submit"
              className="
                w-full mx-auto block
                py-[11px] px-[20px]
                bg-lime-500 hover:bg-lime-400
                !text-navy-900 text-[13px] font-bold
                border-0 rounded-[20px]
                text-center cursor-pointer
                transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('views.registration.sending', 'Enviando...')
                : t('views.reset_password.submit', 'Alterar senha')}
            </button>

            <div className="text-center mt-4">
              <Link
                to="/login"
                className="
                  inline-flex items-center justify-center gap-2 w-full
                  py-[11px] px-[20px]
                  bg-mist-100 hover:bg-mist-300/60
                  !text-navy-800 text-[13px] font-medium
                  border-0 rounded-[20px]
                  transition-colors
                "
              >
                <i className="fa fa-arrow-left" aria-hidden="true"></i> {t('common.back_to_login', 'Voltar para o login')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete?: string;
  autoFocus?: boolean;
  showLabel: string;
  hideLabel: string;
}

/** Campo de senha com botão de alternar visibilidade, no estilo do card. */
function PasswordInput({ value, onChange, placeholder, autoComplete, autoFocus, showLabel, hideLabel }: PasswordInputProps): ReactNode {
  const [visible, setVisible] = useState(false);
  return (
    <div className="mb-5 relative flex items-center">
      <input
        type={visible ? 'text' : 'password'}
        className="
          w-full h-[42px] pl-[17px] pr-[44px]
          border border-solid border-mist-300 rounded-[20px]
          text-[13px] text-navy-800
          bg-white
          font-['Poppins',sans-serif]
          focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500
          placeholder:text-mist-500
        "
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        className="absolute right-[6px] h-[34px] w-[34px] flex items-center justify-center border-0 bg-transparent cursor-pointer text-[15px] text-mist-500 hover:text-navy-800"
      >
        <i className={`fa ${visible ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>
      </button>
    </div>
  );
}
